import { Rights } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ROLE_RIGHTS_GROUPS, ROLE_RIGHTS_OTHER_GROUP_ID, ROLE_RIGHTS_OTHER_TITLE, getPermissionGroupId } from './role-rights-groups';

export interface RoleRightsProps {
    permissions: { [key: string]: string[] };
}

interface RoleRightsEntityRow {
    key: string;
    rights: string[];
}

interface RoleRightsSection {
    id: string;
    title: ReactNode;
    entities: RoleRightsEntityRow[];
}

const STANDARD_RIGHTS = [Rights.READ, Rights.CREATE, Rights.UPDATE, Rights.DELETE] as const;

function isStandardRight(right: string): right is (typeof STANDARD_RIGHTS)[number] {
    return (STANDARD_RIGHTS as readonly string[]).includes(right);
}

function humanizeKey(value: string): string {
    return value
        .replace(/_/g, ' ')
        .replace(/\*/g, 'super admin')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getRightLabel(right: string): ReactNode {
    switch (right) {
        case Rights.READ:
            return CommonMessage.BUTTONS.VIEW;
        case Rights.CREATE:
            return CommonMessage.BUTTONS.CREATE;
        case Rights.UPDATE:
            return (
                <FormattedMessage
                    id='platform.role.role-rights.edit'
                    description='Badge label for the update/edit right on a role'
                    defaultMessage='Edit'
                />
            );
        case Rights.DELETE:
            return CommonMessage.BUTTONS.DELETE;
        default:
            return humanizeKey(right);
    }
}

function getStandardBadgeClassName(right: (typeof STANDARD_RIGHTS)[number]): string {
    if (right === Rights.DELETE) {
        return 'bg-status-danger text-background-surface';
    }
    return 'bg-status-info text-background-surface';
}

const BADGE_SIZE_CLASS =
    'box-border flex h-7 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full px-2 text-sm font-semibold whitespace-nowrap';

function splitRights(rights: string[]): {
    standard: Set<(typeof STANDARD_RIGHTS)[number]>;
    extras: string[];
} {
    const standard = new Set<(typeof STANDARD_RIGHTS)[number]>();
    const extras: string[] = [];

    for (const right of rights) {
        if (isStandardRight(right)) {
            standard.add(right);
        } else {
            extras.push(right);
        }
    }

    extras.sort((a, b) => a.localeCompare(b));
    return { standard, extras };
}

function buildSections(permissions: { [key: string]: string[] }): RoleRightsSection[] {
    const activeEntries = Object.entries(permissions)
        .filter(([, rights]) => rights.length > 0)
        .map(([key, rights]) => ({ key, rights }));

    const sections: RoleRightsSection[] = ROLE_RIGHTS_GROUPS.map((group) => ({
        id: group.id,
        title: group.title,
        entities: activeEntries.filter((entry) => getPermissionGroupId(entry.key) === group.id).sort((a, b) => a.key.localeCompare(b.key))
    })).filter((section) => section.entities.length > 0);

    const otherEntities = activeEntries
        .filter((entry) => getPermissionGroupId(entry.key) === ROLE_RIGHTS_OTHER_GROUP_ID)
        .sort((a, b) => a.key.localeCompare(b.key));

    if (otherEntities.length > 0) {
        sections.push({
            id: ROLE_RIGHTS_OTHER_GROUP_ID,
            title: ROLE_RIGHTS_OTHER_TITLE,
            entities: otherEntities
        });
    }

    return sections;
}

function entityMatchesSearch(entity: RoleRightsEntityRow, query: string): boolean {
    const haystack = [entity.key, humanizeKey(entity.key), ...entity.rights, ...entity.rights.map(humanizeKey)].join(' ').toLowerCase();
    return haystack.includes(query);
}

function filterSections(sections: RoleRightsSection[], search: string): RoleRightsSection[] {
    const query = search.trim().toLowerCase();
    if (!query) return sections;

    return sections
        .map((section) => {
            const sectionMatches = section.id.toLowerCase().includes(query) || humanizeKey(section.id).toLowerCase().includes(query);
            return {
                ...section,
                entities: sectionMatches ? section.entities : section.entities.filter((entity) => entityMatchesSearch(entity, query))
            };
        })
        .filter((section) => section.entities.length > 0);
}

const StandardRightsBadges = ({ standard }: { standard: Set<(typeof STANDARD_RIGHTS)[number]> }) => (
    <div className='grid grid-cols-4 gap-2'>
        {STANDARD_RIGHTS.map((right) => (
            <div
                key={right}
                className='flex h-7 w-20 items-center justify-center'>
                {standard.has(right) ? <span className={`${BADGE_SIZE_CLASS} ${getStandardBadgeClassName(right)}`}>{getRightLabel(right)}</span> : null}
            </div>
        ))}
    </div>
);

const RoleRightsRow = ({ entity }: { entity: RoleRightsEntityRow }) => {
    const { standard, extras } = splitRights(entity.rights);

    return (
        <div className='flex items-center justify-between gap-4 px-4 py-2'>
            <div className='flex min-w-0 flex-col gap-0.5'>
                <dt className='text-foreground-default text-sm font-semibold'>{humanizeKey(entity.key)}</dt>
                {extras.length > 0 && (
                    <p className='text-foreground-muted text-xs font-semibold'>
                        <FormattedMessage
                            id='platform.role.role-rights.custom'
                            description='Label prefix for non-CRUD custom rights listed under an entity in the role rights view'
                            defaultMessage='Custom:'
                        />{' '}
                        {extras.map((right) => humanizeKey(right)).join(', ')}
                    </p>
                )}
            </div>
            <dd className='shrink-0'>
                <StandardRightsBadges standard={standard} />
            </dd>
        </div>
    );
};

export const RoleRights = (props: RoleRightsProps) => {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const sections = useMemo(() => buildSections(props.permissions), [props.permissions]);
    const filteredSections = useMemo(() => filterSections(sections, search), [sections, search]);

    if (sections.length === 0) {
        return (
            <div className='bg-background-surface outline-border-default flex w-full items-center justify-center rounded-md p-8 outline-1 outline-offset-[-1px]'>
                <span className='text-foreground-muted text-sm'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</span>
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-4'>
            <input
                type='search'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={intl.formatMessage({
                    id: 'Forms.SEARCH_PLACEHOLDER',
                    defaultMessage: 'Search...',
                    description: 'Placeholder text for the search input'
                })}
                className='border-border-default bg-background-surface text-foreground-default placeholder:text-foreground-muted w-full rounded-md border px-3 py-2 text-sm outline-none'
            />
            {filteredSections.length === 0 ? (
                <div className='bg-background-surface outline-border-default flex w-full items-center justify-center rounded-md p-8 outline-1 outline-offset-[-1px]'>
                    <span className='text-foreground-muted text-sm'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</span>
                </div>
            ) : (
                filteredSections.map((section) => (
                    <section
                        key={section.id}
                        className='bg-background-surface outline-border-default flex w-full flex-col overflow-hidden rounded-xl outline-1 outline-offset-[-1px]'>
                        <h3 className='text-foreground-default border-border-default border-b px-4 py-3 text-base font-semibold'>{section.title}</h3>
                        <dl className='divide-border-default divide-y'>
                            {section.entities.map((entity) => (
                                <RoleRightsRow
                                    key={entity.key}
                                    entity={entity}
                                />
                            ))}
                        </dl>
                    </section>
                ))
            )}
        </div>
    );
};
